// @ts-check

import { getUnpaidCompanyInvoices } from '@/api/subscription'
import { getTermsOfService } from '@/api/terms'
import {
  LOCAL_STORAGE_KEY_LANDING_PAGE,
  LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
} from '@/core/constants'
import { i18n } from '@/i18n'
import { setupRoutes } from '@/router/routes'
import { store } from '@/store'
import { isAfter } from 'date-fns'
import { createRouter, createWebHistory } from 'vue-router'

/**
 * @type {import('vue-router').RouterScrollBehavior}
 */
const scrollBehavior = (to) => {
  if (to.meta?.scrollToTop) {
    return document
      .getElementsByTagName('main')[0]
      ?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return false
}

export const router = createRouter({
  history: createWebHistory(),
  routes: setupRoutes(),
  scrollBehavior
})

/** @type {import('vue-router').NavigationGuard} */
// eslint-disable-next-line complexity
export const navigationGuard = async (to, from, next) => {
  // Get expiration tokens from local storage to avoid browser multitab problems
  const expirationToken = localStorage.getItem(
    LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
  )
  const expirationTokenExpiredOrMissing = expirationToken
    ? isAfter(new Date(), new Date(expirationToken))
    : true
  const requiresAuth = to.matched.some(
    (route) => route.meta.requiresAuth === true
  )

  // Admin login handles authenticated and unauthenticated user.
  if (to.name === 'admin-login') {
    return next()
  }

  // Remove landing page flag if user manually navigates to other page before
  // authentication.
  if (to.name !== 'login') {
    localStorage.removeItem(LOCAL_STORAGE_KEY_LANDING_PAGE)
  }

  // Log out the user immediately if expirationTokenExpiredOrMissing, in cases:
  // When the user is trying to access a protected route OR
  // when the user is already logged in
  if (
    expirationTokenExpiredOrMissing &&
    (requiresAuth || store.getters['auth/isLoggedIn'])
  ) {
    if (requiresAuth) {
      localStorage.setItem(LOCAL_STORAGE_KEY_LANDING_PAGE, to.name ?? '')
    }

    if (expirationToken) {
      await store.dispatch('auth/logout', {
        message: i18n.global.t('login.system_logout')
      })
    } else {
      await store.dispatch('auth/logout')
    }

    // Cancel the current navigation and let the auth make a new one to login.
    return next(false)
  }

  if (!requiresAuth) {
    // We need to redirect a logged in user to dashboard from
    // non authorization required routes except TOS & not found page
    if (
      store.getters['auth/isLoggedIn'] &&
      to.name !== 'terms-of-service' &&
      to.name !== 'not_found'
    ) {
      // Go to dashboard
      return next({ name: 'dashboard' })
    }

    return next()
  }

  // Init and get flags from company data.
  await store.dispatch('companyData/init')

  const isAdmin = store.getters['companyData/isAdmin']
  const companyIsActive = store.getters['companyData/companyIsActive']
  const isInDemoMode = store.getters['companyData/isInDemoMode']
  const onboardingCompleted = store.getters['companyData/onboardingCompleted']
  const hasSignedPoa = store.getters['companyData/hasSignedPoa']
  const isKycRequired = store.getters['companyData/isKycRequired']
  const hasCompletedRegistrationSteps =
    store.getters['companyData/hasCompletedRegistrationSteps']

  // Route the admin always to user login page.
  if (isAdmin) {
    if (to.name !== 'admin') {
      return next({ name: 'admin' })
    }
    return next()
  }

  // Do not allow normal users to access admin login
  if (!isAdmin && to.name === 'admin') {
    return next({ name: 'dashboard' })
  }

  // Do not allow registered user to access setup pages.
  if (onboardingCompleted && to.name?.match('setup*')) {
    return next({ name: 'dashboard' })
  }

  // Do not allow signed user to access setup pages for other services.
  if (hasSignedPoa && to.name?.match('setup*')) {
    return next({ name: 'dashboard' })
  }

  // Do not allow inactive companies to navigate to entry creation pages.
  if (
    ['new_income', 'new_invoice', 'new_expense'].includes(to.name ?? '') &&
    !companyIsActive
  ) {
    return next({ name: 'dashboard' })
  }

  // Do not care about restrictions and other stuff in demo mode.
  if (isInDemoMode) {
    return next()
  }

  // Check if the user has accepted terms or has unpaid invoices after
  // authentication.
  if (
    ['login', 'registration'].includes(from.name ?? '') &&
    to.name === 'dashboard'
  ) {
    const approvedTermsId = store.getters['companyData/approvedTermsId']
    let latestTerms = null
    let hasAcceptedLatestTerms = true

    try {
      const { data } = await getTermsOfService()
      latestTerms = data
      hasAcceptedLatestTerms = approvedTermsId === latestTerms.id
    } catch (error) {
      console.error(error)
      hasAcceptedLatestTerms = true
    }

    if (!hasAcceptedLatestTerms) {
      return next({
        name: 'accept-terms-of-service',
        params: { id: latestTerms.id }
      })
    }

    // User with onboarding incomplete has KYC questionnaire on onboarding box
    // so do not redirect them to separate KYC questionnaire page
    if (hasCompletedRegistrationSteps && isKycRequired) {
      return next({
        name: 'kyc-questionnaire'
      })
    }

    try {
      const unpaidInvoices = await getUnpaidCompanyInvoices()

      // If the user has unpaid service fees, guide them to subscriptions view
      // where they can be paid.
      if (unpaidInvoices.length) {
        return next({ name: 'subscription_details' })
      }
    } catch (error) {
      console.error(error)
    }
  }

  return next()
}

router.beforeEach(navigationGuard)

export default router
